/**
 * Extracts the first chunk of a string before the first hyphen (already lowercase expected).
 *
 * @example firstHyphenChunk('foo-bar-baz') // 'foo'
 *
 * @param {string} s - The input string from which the first chunk is to be extracted.
 * @returns {string} The first chunk of the string before the hyphen, or an empty string if the input is falsy.
 */
export const firstHyphenChunk = (s: string): string => (s ? s.split('-')[0] : '');

export const parseBool = (str: string | undefined | null): boolean => {
  if (!str) return false;

  const s = str.trim().toLowerCase();

  return s === 'true';
};

/**
 * Validates whether a given string is a valid IPv4 address.
 *
 * The function checks if the input string conforms to the IPv4 address format.
 * An IPv4 address must consist of four numerical segments separated by dots,
 * where each segment is a number between 0 and 255.
 *
 * @param {string} [ip] - The IP address string to validate. This parameter is optional.
 * @returns {boolean} Returns true if the input string is a valid IPv4 address, false otherwise.
 */
export const validateIPAddress = (ip?: string) => {
  if (!ip) return false;
  const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Regex.test(ip)) {
    return ip.split('.').every(part => parseInt(part) <= 255);
  }

  return false;
};

const unitMap: Record<string, string> = {
  Unitless: '',
  Percentage: '%',
  Celcius: '°C',
  Meter: 'm',
  Millimeter: 'mm',
  Kilogram: 'kg',
  Seconds: 's',
  Ampere: 'A',
  Volt: 'V',
  KW: 'kW',
  VA: 'VA',
  VAR: 'VAR',
  Liters: 'L',
  LitersPerHour: 'L/h',
  MgL: 'mg/L',
  UMol: 'µmol',
  Degree: '°',
  Psu: 'PSU',
  Rpm: 'rpm',
};

export const translateUnitLabel = (value: string) => unitMap[value] ?? value;

const isUpperCase = (c?: string): boolean => {
  if (!c) return true;
  return c === c.toUpperCase();
};

const isNumber = (c: string): boolean => !isNaN(Number(c));

const capitalize = (s: string) => {
  if (!s) return '';
  if (s.length <= 2) return s;
  return `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
};

/**
 * Transforms a string by splitting it at uppercase letters and capitalizing the result.
 *
 * The function takes a given string and performs the following transformations:
 * 1. Inserts a space before uppercase letters if they are not followed by another uppercase letter.
 * 2. Keeps consecutive uppercase letters and digits together; inserts a space after a digit when the next character is lowercase (digit→uppercase is split via the uppercase rule).
 * 3. Trims any leading or trailing whitespace.
 * 4. Capitalizes the resulting string.
 *
 * @param {string} s - The input string to transform.
 * @returns {string} The transformed and capitalized string, or an empty string if the input is falsy.
 */
export const capitalizeAndSplitOnUpperCase = (s: string | undefined) => {
  if (!s) return '';

  return capitalize(
    [...s]
      .reduce((res, c, index, arr) => {
        if (isNumber(c)) {
          const next = arr[index + 1];
          if (isNumber(next)) {
            return res + c;
          }
          // Uppercase following a digit gets a leading space from the branch below;
          // lowercase needs a boundary here so runs like `foo2bar` become words.
          if (next && /[a-z]/.test(next)) {
            return `${res + c} `;
          }
          return res + c;
        }
        if (isUpperCase(c)) {
          if (isUpperCase(arr[index + 1])) {
            return res + c;
          }
          return res + ' ' + c;
        }
        return res + c;
      }, '')
      .trim()
  );
};
